<template>
  <LazySection
    :is-height-fixed="!isFetched"
    :placeholder-height="500"
  >
    <section
      v-if="loaderStore.isFiltersLoading || currentList.length"
      ref="guidesBlock"
      class="guides-wrap"
    >
      <div class="wrap">
        <h2
          class="feature-title"
          data-test="guide-block-title"
        >
          {{ headString }}
        </h2>
        <component
          :is="currentComponentSkeleton"
          v-if="isSkeletonActive"
        />
        <component
          :is="currentComponent"
          v-else-if="currentList.length"
          class="guides__list"
          :current-list="currentList"
          :default-sogu-fields="defaultSoguFields"
          :has-tours="false"
        />

        <div
          v-if="showAllButton"
          class="guides__btn"
        >
          <ui-button
            :href="pathToGuides"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            text="Все гиды"
            data-test="guide-block-show-all"
            @click="allGuidesButtonClickHandler"
          />
        </div>
      </div>
    </section>
  </LazySection>
</template>
<script setup lang="ts">
  // Widgets
  import {
    type ExpTypes,
    type SortingTypes,
    useGuidesList,
    type GuideType,
  } from '@/widgets/guidesBlock/api/useGuidesList.ts';
  import ExcursionGuidesListSkeleton from './ExcursionGuidesListSkeleton.vue';
  import ExcursionGuidesSlideSkeleton from './ExcursionGuidesSlideSkeleton.vue';

  // Features
  import { GuidesList } from '@/features/guidesList';
  import { GuidesSlider } from '@/features/guidesSlider';

  // Entities
  import { trackEvent } from '@/entities/sogu';

  // Shared
  import type { CountryInfo, RegionInfo, CityInfo } from '@/shared/api/experience';
  import type { ContextGeoSubject } from '@/shared/types/geography';
  import { useLoaderStore } from '@/shared/store/useLoaderStore.ts';
  import { makeFullLink } from '@/shared/lib/makeFullLink';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';

  // External and shared packages
  import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
  import { useScreenSize } from '@experience/shared';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { computed, ref } from 'vue';
  import { LazySection } from '@/shared/ui/LazySections';

  type MessageSchema = typeof ru;

  const { t } = useI18n<
    {
      message: MessageSchema;
    },
    'ru'
  >({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    slug: string;
    location: string;
    geoObject: CountryInfo | RegionInfo | CityInfo;
    defaultSoguFields: ContextGeoSubject;
  }

  const props = defineProps<Props>();
  const loaderStore = useLoaderStore();

  const { isSkeletonActive } = useListingSkeleton();
  const guidesBlock = ref<HTMLElement | null>(null);

  const { isMobile } = useScreenSize();

  const urlParam = computed(() => {
    const baseParams = {
      page_size: 5,
      sorting: '-review_rating' as SortingTypes,
      guide_types: 'guide' as GuideType,
    };

    return {
      ...baseParams,
      [props.location]: props.slug,
      exp_types: '' as ExpTypes,
    };
  });
  const { guidesList, isFetched } = useGuidesList(urlParam.value);

  const currentList = computed(() => guidesList.value?.results || []);

  const headString = computed(() => {
    const prefix = `${t('guides_section.private_guides')}`;

    return `${prefix} ${slugStr.value}`;
  });

  const slugStr = computed(() => props.geoObject?.in_obj_phrase || '');

  const currentComponent = computed(() => (isMobile.value ? GuidesSlider : GuidesList));
  const currentComponentSkeleton = computed(() =>
    isMobile.value ? ExcursionGuidesSlideSkeleton : ExcursionGuidesListSkeleton,
  );

  const allGuidesButtonClickHandler = () => {
    trackEvent('listing:guides:view-all-button.click', { ...props.defaultSoguFields.soguStamp });
  };

  const showAllButton = computed(
    () => guidesList.value && guidesList.value.count && currentList.value.length < guidesList.value.count,
  );

  const pathToGuides = computed(() => {
    const { slug } = props;

    return makeFullLink(`/experience/${slug}/guides/`);
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .guides-wrap {
    margin-top: 80px;

    .feature-title {
      @include typography.base-800-750-extra;

      @include adaptivity.tablet {
        @include typography.base-700-500-extra;
      }
    }

    .guides__list {
      margin-top: 24px;
    }

    .guides__btn {
      display: flex;
      justify-content: center;
      margin: 40px 0 0;

      .button {
        --btn-width: 297px;
      }

      @include adaptivity.mobile {
        box-sizing: border-box;
        margin-top: 16px;

        .button {
          --btn-width: 100%;
        }
      }
    }

    @include adaptivity.tablet {
      margin-top: 56px;
    }

    @include adaptivity.mobile {
      .feature-title {
        margin-bottom: 16px;
      }
    }
  }
</style>
